import { CSSProperties } from 'react';
import { useRouter } from 'next/router';
import { PATHS } from '@belong/common';
import { LinkBase, Image, Link, Button } from '@belong/ui';
import clsx from 'clsx';
import { trackEventService } from 'src/api/tracking';
import { RegionSelectorButton } from 'src/components/header/region-selector-button/region-selector-button';
import { NAVBAR_ACTIONS } from 'src/features/homeowners/constants';
import {
  GA_ENTRY_POINTS,
  GA_EVENTS,
  GA_EXPERIMENT,
} from 'src/features/rent-estimate/constants';
import { useUser } from 'src/features/users/useUser';
import css from './sticky-navbar.module.css';

type StickyNavbarProps = {
  handlePricingClick: () => void;
  style?: CSSProperties;
};

export function StickyNavbar({
  handlePricingClick,
  style,
}: StickyNavbarProps): JSX.Element {
  const { user } = useUser();
  const router = useRouter();

  const handleRentEstimateClick = async () => {
    await trackEventService({
      eventName: GA_EVENTS.FLOW_STARTED,
      metadata: {
        ...(user?.userId && { userId: user.userId }),
        variant: GA_ENTRY_POINTS.HO_STICKY_NAV_BAR,
        experiment: GA_EXPERIMENT.RENT_ESTIMATE,
      },
    });

    router.push(PATHS.RENT_ESTIMATE_FLOW);
  };

  return (
    <div
      className={clsx(
        'bg-dark-navy/70 fixed top-0 z-50 w-full backdrop-blur-md',
        css.container,
      )}
      style={style}
    >
      <div className="container flex h-full items-center gap-[6px] xl:justify-between xl:gap-0">
        <div className="flex min-w-0 flex-1 items-center xl:flex-none">
          <div className="mr-xl hidden xl:flex">
            <LinkBase href="/" className={css.logo} aria-label="Go To Homepage">
              <Image
                src="/logos/belong_logo_web_white.svg"
                alt=""
                aria-hidden="true"
              />
            </LinkBase>
          </div>
          <div className={css.regionButtonContainer}>
            <RegionSelectorButton color="white" className="w-full xl:w-auto" />
          </div>
        </div>
        <div className="flex flex-row-reverse items-center xl:flex-row">
          <div className="contents md:hidden">
            <div className="text-center">
              <Button
                variant="text"
                buttonType="secondary"
                size="auto"
                onClick={handleRentEstimateClick}
              >
                <span className="text-p1 leading-p1">{`Get\u00a0Rent\u00a0Estimate`}</span>
              </Button>
            </div>
          </div>
          <div className="hidden md:contents">
            <div className="xl:mr-xl text-center">
              {NAVBAR_ACTIONS.ASK_A_QUESTION()}
            </div>
          </div>
          <div className="mr-xs xl:mr-xl">
            {NAVBAR_ACTIONS.PRICING(handlePricingClick)}
          </div>
          <div className="mr-xl hidden xl:block">
            <Button
              variant="text"
              buttonType="secondary"
              size="auto"
              onClick={handleRentEstimateClick}
            >
              <span className="text-p1 leading-p1">{`Get\u00a0Rent\u00a0Estimate`}</span>
            </Button>
          </div>
          <div className="hidden xl:contents">
            <Link
              href="/homeowner/onboard/property-address"
              variant="solid"
              className="h-xl"
              size="small"
            >
              <span className="text-p1 leading-p1">Get Started</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
